import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sun, Moon } from 'lucide-react';
import { HallmarkSlide1 } from '../components/hallmark-slides/HallmarkSlide1';
import { HallmarkSlide2 } from '../components/hallmark-slides/HallmarkSlide2';
import { HallmarkSlide3 } from '../components/hallmark-slides/HallmarkSlide3';
import { HallmarkSlide4 } from '../components/hallmark-slides/HallmarkSlide4';
import { HallmarkSlide5 } from '../components/hallmark-slides/HallmarkSlide5';
import { HallmarkSlide6 } from '../components/hallmark-slides/HallmarkSlide6';
import { HallmarkSlide7 } from '../components/hallmark-slides/HallmarkSlide7';
const slides = [HallmarkSlide1, HallmarkSlide2, HallmarkSlide3, HallmarkSlide4, HallmarkSlide5, HallmarkSlide6, HallmarkSlide7];
export function HallmarkDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isDark, setIsDark] = useState(false);
  const paginate = (newDirection: number) => {
    const nextSlide = currentSlide + newDirection;
    if (nextSlide >= 0 && nextSlide < slides.length) {
      setDirection(newDirection);
      setCurrentSlide(nextSlide);
    }
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        paginate(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        paginate(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);
  const CurrentSlideComponent = slides[currentSlide];
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  return <div className={`w-full h-screen overflow-hidden relative font-sans transition-colors duration-300 ${isDark ? 'bg-[#1D3C58] text-white selection:bg-heliona-mint/30' : 'bg-heliona-cream text-heliona-charcoal selection:bg-heliona-mint/30'}`} data-id="element-920">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-921">
        {isDark ? <>
            <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-heliona-mint/5 rounded-full blur-3xl" data-id="element-922" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-heliona-mint/5 rounded-full blur-3xl" data-id="element-923" />
          </> : <>
            <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-heliona-mint/5 rounded-full blur-3xl" data-id="element-924" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-heliona-pastel-blue/30 rounded-full blur-3xl" data-id="element-925" />
          </>}
      </div>

      {/* Header */}
      {currentSlide > 0 && currentSlide < slides.length - 1 && <header className="absolute top-0 left-0 w-full p-8 flex justify-between items-center z-20" data-id="element-926">
          <div className="flex items-center" data-id="element-927">
            <img src={isDark ? 'https://cdn.magicpatterns.com/uploads/siQDa69uZkA61S2PiBLFML/Heliona_Logo_-_Dark.png' : 'https://cdn.magicpatterns.com/uploads/gsWfp7Yqa2Z6SBgP3SbdyL/HELIONA_transparent.png'} alt="Heliona Logo" className="h-8 sm:h-10" data-id="element-928" />
          </div>
          <div className={`text-sm font-medium uppercase tracking-widest ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-929">
            Confidential
          </div>
        </header>}

      {/* Theme Toggle */}
      <motion.div className="absolute z-30 bottom-8 right-8" data-id="element-930">
        <button onClick={() => setIsDark(!isDark)} className={`relative flex items-center gap-0.5 px-0.5 py-0.5 rounded-full transition-all duration-300 ${isDark ? 'bg-heliona-navy/80 border border-white/20' : 'bg-white/80 border border-heliona-charcoal/20'} backdrop-blur-sm shadow-lg hover:shadow-xl`} aria-label="Toggle theme" data-id="element-931">
          <div className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ${!isDark ? 'bg-heliona-mint text-heliona-navy shadow-md' : 'text-white/40'}`} data-id="element-932">
            <Sun className="w-3.5 h-3.5" data-id="element-933" />
          </div>
          <div className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ${isDark ? 'bg-heliona-mint text-heliona-navy shadow-md' : 'text-heliona-charcoal/40'}`} data-id="element-934">
            <Moon className="w-3.5 h-3.5" data-id="element-935" />
          </div>
        </button>
      </motion.div>

      {/* Main Content Area */}
      <main className="relative w-full h-full flex items-center justify-center z-10" data-id="element-936">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-937">
          <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.2
          }
        }} className="absolute w-full h-full" data-id="element-938">
            <CurrentSlideComponent isDark={isDark} data-id="element-939" />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Navigation Controls */}
      <div className="absolute inset-y-0 left-0 w-24 flex items-center justify-start pl-4 z-20 group" data-id="element-940">
        <button onClick={() => paginate(-1)} disabled={currentSlide === 0} className={`p-3 rounded-full transition-all disabled:opacity-0 disabled:cursor-not-allowed transform -translate-x-4 group-hover:translate-x-0 ${isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white' : 'bg-heliona-charcoal/5 text-heliona-charcoal/50 hover:bg-heliona-charcoal/10 hover:text-heliona-charcoal'}`} data-id="element-941">
          <ChevronLeft className="w-8 h-8" data-id="element-942" />
        </button>
      </div>

      <div className="absolute inset-y-0 right-0 w-24 flex items-center justify-end pr-4 z-20 group" data-id="element-943">
        <button onClick={() => paginate(1)} disabled={currentSlide === slides.length - 1} className={`p-3 rounded-full transition-all disabled:opacity-0 disabled:cursor-not-allowed transform translate-x-4 group-hover:translate-x-0 ${isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white' : 'bg-heliona-charcoal/5 text-heliona-charcoal/50 hover:bg-heliona-charcoal/10 hover:text-heliona-charcoal'}`} data-id="element-944">
          <ChevronRight className="w-8 h-8" data-id="element-945" />
        </button>
      </div>

      {/* Footer / Progress */}
      <footer className="absolute bottom-0 left-0 w-full p-8 flex justify-center items-center z-20" data-id="element-946">
        <div className="flex gap-3" data-id="element-947">
          {slides.map((_, index) => <button key={index} onClick={() => {
          setDirection(index > currentSlide ? 1 : -1);
          setCurrentSlide(index);
        }} className={`h-1.5 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-heliona-mint' : isDark ? 'w-2 bg-white/20 hover:bg-white/40' : 'w-2 bg-heliona-charcoal/20 hover:bg-heliona-charcoal/40'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-948" />)}
        </div>
      </footer>
    </div>;
}